import type { ChainId } from '#lib/models/index.js'

export type ChainSocials = {
	website: string
	twitter?: string
	discord?: string
	telegram?: string | string[]
	github?: string | string[]
	reddit?: string
	youtube?: string
}

/* eslint sort-keys: ["error", "asc", { caseSensitive: true, natural: true }] */
export const CHAINS_SOCIALS: Record<ChainId, ChainSocials> = {
	algorand_chain: {
		discord: 'https://discord.gg/algorand',
		github: ['https://github.com/algorand/', 'https://github.com/algorandfoundation'],
		reddit: 'https://www.reddit.com/r/algorand/',
		telegram: 'https://t.me/algorand',
		twitter: 'https://x.com/algorand',
		website: 'https://algorand.com/',
		youtube: 'https://www.youtube.com/algorand',
	},
	allora_chain: {
		twitter: 'https://x.com/AlloraNetwork',
		website: 'https://www.allora.network/',
	},
	aptos_chain: {
		discord: 'https://discord.com/invite/aptosnetwork',
		github: ['https://github.com/aptos-foundation', 'https://github.com/aptos-labs'],
		reddit: 'https://www.reddit.com/r/Aptos/',
		telegram: 'https://t.me/aptos',
		twitter: 'https://x.com/aptos',
		website: 'https://aptosfoundation.org/',
		youtube: 'https://www.youtube.com/@aptosnetwork',
	},
	arbitrum_chain: {
		discord: 'https://discord.gg/arbitrum',
		github: ['https://github.com/OffchainLabs', 'https://github.com/ArbitrumFoundation'],
		reddit: 'https://www.reddit.com/r/Arbitrum/',
		telegram: 'https://t.me/arbitrum',
		twitter: 'https://x.com/arbitrum',
		website: 'https://arbitrum.io/',
		youtube: 'https://www.youtube.com/arbitrum',
	},
	arc_chain: {
		twitter: 'https://x.com/arc',
		website: 'https://www.arc.network/',
	},
	avalanche_chain: {
		discord: 'https://discord.com/invite/avax',
		github: ['https://github.com/ava-labs', 'https://github.com/avalanche-foundation'],
		reddit: 'https://www.reddit.com/r/Avax/',
		telegram: 'https://t.me/avalancheavax',
		twitter: 'https://x.com/avax',
		website: 'https://www.avax.network/',
		youtube: 'https://www.youtube.com/Avalancheavax',
	},
	aztec_chain: {
		twitter: 'https://x.com/aztecnetwork',
		website: 'https://aztec.network/',
	},
	babylon_chain: {
		twitter: 'https://x.com/babylonlabs_io',
		website: 'https://babylonlabs.io/',
	},
	base_chain: {
		discord: 'https://discord.com/invite/buildonbase',
		github: 'https://github.com/base',
		twitter: 'https://x.com/base',
		website: 'https://base.org/',
	},
	beam_chain: {
		twitter: 'https://x.com/BuildOnBeam',
		website: 'https://onbeam.com/',
	},
	bitcoin_chain: {
		github: ['https://github.com/bitcoin', 'https://github.com/bitcoin-core'],
		reddit: 'https://www.reddit.com/r/Bitcoin/',
		twitter: 'https://x.com/Bitcoin',
		website: 'https://bitcoin.org/',
	},
	bnb_chain: {
		discord: 'https://discord.gg/QRTQvfhADQ',
		github: 'https://github.com/bnb-chain',
		reddit: 'https://www.reddit.com/r/bnbchainofficial/',
		telegram: 'https://t.me/bnbchain',
		twitter: 'https://x.com/BNBChain',
		website: 'https://www.bnbchain.org/',
		youtube: 'https://www.youtube.com/channel/UCG9fZu6D4I83DStktBV0Ryw',
	},
	bsquared_chain: {
		twitter: 'https://x.com/BsquaredNetwork',
		website: 'https://www.bsquared.network/',
	},
	bsv_chain: {
		discord: 'https://discord.com/invite/bsv',
		github: [
			'https://github.com/bitcoin-sv',
			'https://github.com/bsv-blockchain-demos',
			'https://github.com/bsv-blockchain-devrel',
			'https://github.com/bsv-blockchain',
			'https://github.com/bsvhackathon',
		],
		twitter: 'https://x.com/BSVBlockchain',
		website: 'https://bsvblockchain.org/',
		youtube: 'https://www.youtube.com/@bsv_blockchain',
	},
	cardano_chain: {
		github: ['https://github.com/cardano-foundation', 'https://github.com/IntersectMBO'],
		reddit: 'https://www.reddit.com/r/cardano/',
		telegram: 'https://t.me/CardanoAnnouncements',
		twitter: 'https://x.com/Cardano',
		website: 'https://cardano.org/',
		youtube: 'https://www.youtube.com/@CardanoFoundation',
	},
	cosmos_chain: {
		twitter: 'https://x.com/cosmos',
		website: 'https://cosmos.network/',
	},
	creditcoin_chain: {
		twitter: 'https://x.com/creditcoin',
		website: 'https://creditcoin.org/',
	},
	cronos_chain: {
		discord: 'https://discord.com/invite/pahqHz26q4',
		github: 'https://github.com/crypto-org-chain',
		telegram: 'https://t.me/Cronos_Announcements',
		twitter: 'https://x.com/cronos_chain',
		website: 'https://cronos.org/',
	},
	dusk_chain: {
		twitter: 'https://x.com/duskfoundation',
		website: 'https://dusk.network/',
	},
	ecash_chain: {
		discord: 'https://discord.gg/ecash-official-852595915159896114',
		github: 'https://github.com/bitcoin-abc/',
		reddit: 'https://reddit.com/r/ecash',
		telegram: 'https://t.me/ecash',
		twitter: 'https://x.com/eCash',
		website: 'https://e.cash/',
		youtube: 'https://www.youtube.com/eCashOfficial',
	},
	ethereum_chain: {
		discord: 'https://discord.gg/ethereum-org',
		github: 'https://github.com/ethereum',
		reddit: 'https://www.reddit.com/r/ethereum/',
		twitter: 'https://x.com/ethdotorg',
		website: 'https://ethereum.org/',
		youtube: 'https://www.youtube.com/@EthereumProtocol',
	},
	etherlink_chain: {
		discord: 'https://discord.gg/etherlink',
		github: 'https://github.com/etherlinkcom',
		twitter: 'https://x.com/etherlink',
		website: 'https://www.etherlink.com/',
	},
	flare_chain: {
		twitter: 'https://x.com/FlareNetworks',
		website: 'https://flare.network/',
	},
	fogo_chain: {
		discord: 'https://discord.gg/FogoChain',
		twitter: 'https://x.com/fogochain',
		website: 'https://www.fogo.io/',
	},
	gravity_chain: {
		twitter: 'https://x.com/GravityChain',
		website: 'https://gravity.xyz/',
	},
	hedera_chain: {
		discord: 'https://hedera.com/discord',
		github: ['https://github.com/hashgraph', 'https://github.com/hiero-ledger'],
		reddit: 'https://www.reddit.com/r/Hedera/',
		telegram: 'https://t.me/hederahashgraph',
		twitter: 'https://x.com/hedera',
		website: 'https://hedera.com/',
		youtube: 'https://www.youtube.com/@HederaHashgraph',
	},
	hyperliquid_chain: {
		twitter: 'https://x.com/HyperliquidX',
		website: 'https://hyperliquid.xyz/',
	},
	icp_chain: {
		github: [
			'https://github.com/dfinity',
			'https://github.com/dfinity-lab',
			'https://github.com/dfinity-ops',
			'https://github.com/utopia-icp',
		],
		reddit: 'https://www.reddit.com/r/dfinity/',
		telegram: 'https://t.me/+m8tiEFaaNR8xNjNl',
		twitter: 'https://x.com/dfinity',
		website: 'https://internetcomputer.org/',
		youtube: 'https://www.youtube.com/@DFINITY',
	},
	immutable_x_chain: {
		twitter: 'https://x.com/Immutable',
		website: 'https://www.immutable.com/',
	},
	ink_chain: {
		twitter: 'https://x.com/inkonchain',
		website: 'https://inkonchain.com/',
	},
	iotex_chain: {
		discord: 'https://discord.com/invite/q5eYde2CU7',
		github: 'https://github.com/iotexproject',
		reddit: 'https://www.reddit.com/r/IoTeX/',
		telegram: 'https://t.me/IoTeXGroup',
		twitter: 'https://x.com/iotex_io',
		website: 'https://iotex.io/',
		youtube: 'https://www.youtube.com/@IoTeXOfficialChannel',
	},
	kaia_chain: {
		discord: 'https://discord.com/invite/kaiachain',
		github: 'https://github.com/kaiachain',
		reddit: 'https://www.reddit.com/r/kaiachain/',
		telegram: 'https://t.me/kaiachain_en',
		twitter: 'https://x.com/kaiachain',
		website: 'https://www.kaia.io/',
		youtube: 'https://www.youtube.com/@kaiachain',
	},
	kava_chain: {
		twitter: 'https://x.com/KAVA_CHAIN',
		website: 'https://www.kava.io',
	},
	keeta_mainnet_chain: {
		discord: 'https://discord.gg/keeta',
		github: 'https://github.com/keetanetwork',
		twitter: 'https://x.com/keetanetwork',
		website: 'https://keeta.com/',
	},
	keeta_testnet_chain: {
		discord: 'https://discord.gg/keeta',
		github: 'https://github.com/keetanetwork',
		twitter: 'https://x.com/keetanetwork',
		website: 'https://keeta.com/',
	},
	kite_ai_chain: {
		twitter: 'https://x.com/GoKiteAI',
		website: 'https://gokite.ai/',
	},
	kusama_chain: {
		discord: 'https://kusa.ma/discord',
		github: ['https://github.com/paritytech', 'https://github.com/polkadot-fellows'],
		reddit: 'https://www.reddit.com/r/Kusama/',
		twitter: 'https://x.com/kusamanetwork',
		website: 'https://kusama.network/',
		youtube: 'https://www.youtube.com/@KusamaNetwork',
	},
	lens_chain: {
		twitter: 'https://x.com/LC',
		website: 'https://lens.xyz/',
	},
	linea_chain: {
		twitter: 'https://x.com/LineaBuild',
		website: 'https://linea.build/',
	},
	mantle_chain: {
		twitter: 'https://x.com/0xMantle',
		website: 'https://www.mantle.xyz/',
	},
	megaeth_chain: {
		twitter: 'https://x.com/megaeth_labs',
		website: 'https://www.megaeth.com/',
	},
	monad_chain: {
		twitter: 'https://x.com/monad_xyz',
		website: 'https://www.monad.xyz/',
	},
	multiversx_chain: {
		discord: 'https://discord.gg/multiversxbuilders',
		github: 'https://github.com/MultiversX',
		reddit: 'https://www.reddit.com/r/MultiversXOfficial/',
		telegram: 'https://t.me/MultiversXAnn',
		twitter: 'https://x.com/MultiversX',
		website: 'https://multiversx.com/',
		youtube: 'https://www.youtube.com/@MultiversXOfficial',
	},
	n1_chain: {
		twitter: 'https://x.com/N1Chain',
		website: 'https://www.n1.xyz/',
	},
	near_chain: {
		discord: 'http://near.chat/',
		github: 'https://github.com/near',
		reddit: 'https://www.reddit.com/r/nearprotocol/',
		telegram: 'https://t.me/cryptonear',
		twitter: 'https://x.com/nearprotocol',
		website: 'https://near.org/',
		youtube: 'https://www.youtube.com/@NEARProtocol',
	},
	neo_chain: {
		twitter: 'https://x.com/neo_blockchain',
		website: 'https://neo.org/',
	},
	optimism_chain: {
		discord: 'https://discord.gg/optimism',
		github: 'https://github.com/ethereum-optimism',
		twitter: 'https://x.com/optimism',
		website: 'https://www.optimism.io/',
	},
	pharos_chain: {
		twitter: 'https://x.com/pharos_network',
		website: 'https://www.pharosnetwork.xyz/',
	},
	plasma_chain: {
		twitter: 'https://x.com/PlasmaFDN',
		website: 'https://www.plasma.to/',
	},
	plume_chain: {
		twitter: 'https://x.com/plumenetwork',
		website: 'https://plume.org/',
	},
	polkadot_chain: {
		discord: 'https://dot.li/discord',
		github: ['https://github.com/paritytech', 'https://github.com/polkadot-fellows'],
		reddit: 'https://www.reddit.com/r/polkadot',
		telegram: 'https://t.me/PolkadotOfficial?ref=cms.polkadot.network',
		twitter: 'https://x.com/Polkadot',
		website: 'https://polkadot.network/',
		youtube: 'https://www.youtube.com/@PolkadotNetwork',
	},
	polygon_chain: {
		discord: 'https://discord.com/invite/0xPolygon',
		github: ['https://github.com/maticnetwork/', 'https://github.com/0xPolygon'],
		reddit: 'https://www.reddit.com/r/polygonnetwork/',
		telegram: 'https://t.me/polygonofficial',
		twitter: 'https://x.com/0xPolygon',
		website: 'https://polygon.technology/',
	},
	quranium_chain: {
		twitter: 'https://x.com/quranium_org',
		website: 'https://www.quranium.org/',
	},
	redbelly_chain: {
		discord: 'https://discord.com/invite/redbelly',
		github: 'https://github.com/redbellynetwork',
		telegram: 'https://t.me/redbellychat',
		twitter: 'https://x.com/RedbellyNetwork',
		website: 'https://redbelly.network/',
		youtube: 'https://www.youtube.com/@redbellynetwork',
	},
	rise_chain: {
		twitter: 'https://x.com/rise_chain',
		website: 'https://risechain.com/',
	},
	ritual_chain: {
		twitter: 'https://x.com/ritualnet',
		website: 'https://ritual.net/',
	},
	ronin_chain: {
		twitter: 'https://x.com/Ronin_Network',
		website: 'https://roninchain.com/',
	},
	sei_chain: {
		discord: 'https://discord.com/invite/sei',
		github: 'https://github.com/sei-protocol',
		telegram: 'https://t.me/seinetwork',
		twitter: 'https://x.com/SeiNetwork',
		website: 'https://www.sei.io/',
	},
	solana_chain: {
		discord: 'https://solana.com/discord',
		github: ['https://github.com/solana-foundation', 'https://github.com/solana-labs'],
		reddit: 'https://solana.com/reddit',
		telegram: 'https://solana.com/telegram',
		twitter: 'https://solana.com/twitter',
		website: 'https://solana.com/',
		youtube: 'https://solana.com/youtube',
	},
	solayer_chain: {
		twitter: 'https://x.com/solayer_labs',
		website: 'https://solayer.org/',
	},
	somnia_chain: {
		discord: 'https://discord.gg/somnia',
		reddit: 'https://www.reddit.com/r/SomniaNetwork/',
		telegram: 'https://t.me/somnianetwork',
		twitter: 'https://x.com/Somnia_Network',
		website: 'https://somnia.network/',
	},
	sonic_chain: {
		discord: 'https://discord.gg/3Ynr2QDSnB',
		github: 'https://github.com/0xsoniclabs',
		reddit: 'https://www.reddit.com/r/0xSonic/',
		telegram: 'https://t.me/Sonic_English',
		twitter: 'https://x.com/SonicLabs',
		website: 'https://www.soniclabs.com/',
	},
	soon_chain: {
		twitter: 'https://x.com/soon_svm',
		website: 'https://soo.network/',
	},
	starknet_chain: {
		discord: 'https://starknet.io/discord',
		github: 'https://github.com/starknet-io',
		twitter: 'https://x.com/Starknet',
		website: 'https://www.starknet.io/',
		youtube: 'https://www.youtube.com/@starkware_ltd',
	},
	stellar_chain: {
		discord: 'https://discord.com/invite/zVYdY3ktTn',
		github: 'https://github.com/stellar',
		reddit: 'https://www.reddit.com/r/Stellar/',
		telegram: 'https://t.me/StellarLumens',
		twitter: 'https://x.com/StellarOrg',
		website: 'https://stellar.org/',
		youtube: 'https://www.youtube.com/@StellarDevelopmentFoundation',
	},
	sui_chain: {
		discord: 'https://discord.com/invite/sui',
		github: ['https://github.com/sui-foundation', 'https://github.com/MystenLabs'],
		reddit: 'https://www.reddit.com/r/sui/',
		twitter: 'https://x.com/SuiNetwork',
		website: 'https://sui.io/',
		youtube: 'https://www.youtube.com/@Sui-Network',
	},
	tezos_chain: {
		discord: 'https://discord.com/invite/tezos-blockchain-699325006928281720',
		github: ['https://github.com/tezos', 'https://github.com/trilitech'],
		reddit: 'https://www.reddit.com/r/tezos',
		telegram: ['https://t.me/TezosPlatform', 'https://t.me/TezosAnnouncements'],
		twitter: 'https://x.com/tezos',
		website: 'https://tezos.com/',
		youtube: 'https://www.youtube.com/@Tezos',
	},
	tezos_ecosystem_chain: {
		discord: 'https://discord.com/invite/tezos-blockchain-699325006928281720',
		github: [
			'https://github.com/tezos',
			'https://github.com/etherlinkcom',
			'https://github.com/trilitech',
		],
		reddit: 'https://www.reddit.com/r/tezos',
		telegram: ['https://t.me/TezosPlatform', 'https://t.me/TezosAnnouncements'],
		twitter: 'https://x.com/tezos',
		website: 'https://tezos.com/',
		youtube: 'https://www.youtube.com/@Tezos',
	},
	theta_chain: {
		twitter: 'https://x.com/Theta_Network',
		website: 'https://www.thetatoken.org/',
	},
	ton_chain: {
		github: [
			'https://github.com/ton-org',
			'https://github.com/ton-blockchain',
			'https://github.com/ton-connect',
			'https://github.com/ton-studio',
			'https://github.com/ton-community',
		],
		telegram: 'https://t.me/toncoin',
		twitter: 'https://x.com/ton_blockchain',
		website: 'https://ton.org/',
	},
	tron_chain: {
		discord: 'https://discord.gg/hqKvyAM',
		github: 'https://github.com/tronprotocol',
		reddit: 'https://www.reddit.com/r/Tronix/',
		telegram: 'https://t.me/tronnetworkEN',
		twitter: 'https://x.com/trondao',
		website: 'https://tron.network/',
		youtube: 'https://www.youtube.com/@TRONDAOofficial',
	},
	vechain_chain: {
		twitter: 'https://x.com/vechainofficial',
		website: 'https://vechain.org/',
	},
	wanchain_chain: {
		github: 'https://github.com/wanchain',
		reddit: 'https://www.reddit.com/r/wanchain/',
		telegram: 'https://t.me/WanchainCHAT',
		twitter: 'https://x.com/wanchain_org',
		website: 'https://www.wanchain.org/',
		youtube: 'https://www.youtube.com/@Wanchain',
	},
	waterfall_chain: {
		discord: 'https://discord.gg/Nwb8aR2XvR',
		github: 'https://github.com/waterfall-network',
		telegram: 'https://t.me/waterfall_network',
		twitter: 'https://x.com/waterfall_dag',
		website: 'https://waterfall.network/',
		youtube: 'https://www.youtube.com/@waterfall_network',
	},
	world_chain: {
		twitter: 'https://x.com/worldcoin',
		website: 'https://world.org/world-chain',
	},
	xdc_chain: {
		twitter: 'https://x.com/XDC_Network_',
		website: 'https://www.xinfin.org/',
	},
	xrpl_chain: {
		twitter: 'https://x.com/Ripple',
		website: 'https://xrpl.org/',
	},
	zksync_chain: {
		twitter: 'https://x.com/zksync',
		website: 'https://www.zksync.io/',
	},
}
